import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { fetchShareAnalytics, shareUrl, type ShareAnalyticsRow } from "@/lib/share";

const money = (cents: number) => `$${(cents / 100).toFixed(0)}`;

/** Per-link share performance: which blueprint URLs actually drive purchases. */
export function ShareAnalyticsPanel() {
  const [rows, setRows] = useState<ShareAnalyticsRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchShareAnalytics());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const totals = (rows ?? []).reduce(
    (acc, r) => ({
      views: acc.views + r.views,
      purchases: acc.purchases + r.purchases,
      revenue: acc.revenue + r.revenue_cents,
    }),
    { views: 0, purchases: 0, revenue: 0 },
  );

  return (
    <section className="rounded-xl border border-border bg-card p-5" aria-labelledby="share-analytics">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 id="share-analytics" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            share performance
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {totals.views} views · {totals.purchases} purchases · {money(totals.revenue)} attributed
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
          Refresh
        </Button>
      </div>

      {error && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
          {error}
        </div>
      )}

      {!error && rows === null && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!error && rows?.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No shared blueprints yet — use Share on any trend card to create your first link.
        </p>
      )}

      {!!rows?.length && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Share link performance by blueprint</caption>
            <thead>
              <tr className="text-left font-mono text-[11px] uppercase text-muted-foreground">
                <th scope="col" className="py-2 pr-3">Blueprint</th>
                <th scope="col" className="py-2 pr-3 text-right">Views</th>
                <th scope="col" className="py-2 pr-3 text-right">Visitors</th>
                <th scope="col" className="py-2 pr-3 text-right">PDFs</th>
                <th scope="col" className="py-2 pr-3 text-right">Emails</th>
                <th scope="col" className="py-2 pr-3 text-right">Signups</th>
                <th scope="col" className="py-2 pr-3 text-right">Purchases</th>
                <th scope="col" className="py-2 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const expired = r.expires_at && new Date(r.expires_at) < new Date();
                return (
                  <tr key={r.share_id} className="border-t border-border/60">
                    <td className="py-2 pr-3 max-w-[220px]">
                      <a
                        href={shareUrl(r.slug)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block truncate underline decoration-dotted"
                        title={r.headline ?? r.trend_title}
                      >
                        {r.headline ?? r.trend_title}
                      </a>
                      <span className="font-mono text-[10px] text-muted-foreground">
                        /b/{r.slug}
                        {expired ? " · expired" : !r.is_active ? " · disabled" : ""}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.views}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.unique_visitors}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.pdf_downloads}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.emails_sent}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.signups}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.purchases}</td>
                    <td className="py-2 text-right tabular-nums">{money(r.revenue_cents)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
